import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { gameTypes, isContestGamesWithSecondaryUgc, isUgcGames } from 'config/promoGameTypes';
import { getEntryVerifyStatus } from 'config/entryVerifyStatus';
import { isJSONParsable, isObject } from 'utils/objectManipulate';
import { utcDTStr2LocalDTStr } from 'utils/dateTime';
import { componentStylePropTypes } from 'client/gameFormBased/utils/propTypes';

import useClientResultCollaspe from 'client/hooks/useClientResultCollaspe';

import ResultInfoInvoice from 'client/components/Result/EntriesInfo/customResult/ResultInfoInvoice';
import ResultUGC from 'client/components/Result/EntriesInfo/customResult/ResultUGC';
import ResultContest from 'client/components/Result/EntriesInfo/customResult/ResultContest';
import ResultComment from 'client/components/Result/EntriesInfo/customResult/ResultComment';
import CollapseWrapper from 'client/components/Result/EntriesInfo/customResult/CollapseWrapper';
import SerialDateInfo from 'client/components/Result/EntriesInfo/SerialDateInfo';
import ResultInfoList from 'client/components/Result/EntriesInfo/customResult/ResultInfoList';
import ResultValidStatus from 'client/components/Result/EntriesInfo/customResult/ResultValidStatus';

function EntriesList({ items = [], gameType, componentStyle, needShowFormBtn = false }) {
    const { enableVerifyMechanism } = useSelector(s => s.settingPromo);
    const { id: signUpFormId } = useSelector(s => s.signUpForm);
    const { formId: gameFormId } = useSelector(s => s.settingGame);
    const isShowValidStatus = Boolean(enableVerifyMechanism);
    const { Comp = () => false, needCollapse = false } = getCustomResultByGameType(
        gameType,
        isShowValidStatus
    );
    const { updateCollapse = () => {}, openTarget = 0 } = useClientResultCollaspe(
        needCollapse,
        items.length - 1
    );
    const parseData = data => (isObject(data) ? data : JSON.parse(data));

    return items.map((item, index) => {
        const {
            flow: { gameResult: gameResultCreatedAt, qualified = false, formSignUp } = {},
            deletedAt,
            gameResult,
            id,
            uuid: entryUuid
        } = item;
        const validStatus = getEntryVerifyStatus({ deletedAt, qualified });
        const resultCreatedAt = Boolean(gameResultCreatedAt)
            ? dayjs(utcDTStr2LocalDTStr(gameResultCreatedAt)).format('YYYY-MM-DD HH:mm')
            : 'YYYY-MM-DD HH:mm';
        const result = isJSONParsable(gameResult) ? parseData(gameResult) : gameResult ?? {};
        const isExpand = openTarget === index;
        const showFormBtn =
            needShowFormBtn &&
            ((gameType === gameTypes.SURVEY && gameResultCreatedAt) || Boolean(formSignUp));
        const formIds = {
            gameFormId: gameType === gameTypes.SURVEY && gameResultCreatedAt ? gameFormId : null,
            signUpFormId
        };
        return (
            <div key={id}>
                {!needCollapse && (
                    <SerialDateInfo
                        id={id}
                        date={resultCreatedAt}
                        showFormBtn={Boolean(showFormBtn)}
                        formIds={formIds}
                        entryUuid={entryUuid}
                    >
                        <Comp result={result} validStatus={validStatus} gameType={gameType} />
                    </SerialDateInfo>
                )}
                {needCollapse && (
                    <CollapseWrapper
                        isCollapse={needCollapse}
                        updateCollapse={updateCollapse}
                        openIndex={index}
                        openTarget={openTarget}
                        disable={isExpand}
                    >
                        <Comp
                            {...{
                                isExpand: isExpand,
                                isMultiple: items.length > 1,
                                serialNo: id,
                                resultCreatedAt,
                                componentStyle,
                                validStatus,
                                resultInfo: result,
                                gameType
                            }}
                        />
                    </CollapseWrapper>
                )}
            </div>
        );
    });
}

EntriesList.propTypes = {
    data: PropTypes.object,
    gameType: PropTypes.number,
    promoId: PropTypes.number,
    componentStyle: componentStylePropTypes
};

export default EntriesList;

function getCustomResultByGameType(gameType, isShowValidStatus) {
    switch (true) {
        case gameType === gameTypes.PURCHASE_REWARD:
        case gameType === gameTypes.ORDER_REWARD:
            return { Comp: ResultInfoList };
        case gameType === gameTypes.INVOICE:
            return { Comp: ResultInfoInvoice };
        case gameType === gameTypes.COMMENT:
            return { Comp: ResultComment, needCollapse: true };
        case isContestGamesWithSecondaryUgc(gameType):
            return { Comp: ResultContest, needCollapse: true };
        case isUgcGames(gameType):
            return { Comp: ResultUGC, needCollapse: true };
        case isShowValidStatus:
            return { Comp: ResultValidStatus };
        default:
            return {};
    }
}
